import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import App from 'antd/lib/app';
import { API } from 'src/domain/api';
import { QueryKey } from 'src/query-client';
import { IMarketAward, MarketAwardStatus } from './interface';
import { calcMarketAwardEarnings, isMarketAwardExpired } from './market-award';

interface ICaisoMarketAwardsQuery {
    start: Date;
    end: Date;
}

const fetchCaisoMarketAwardsList = async (pageQuery: ICaisoMarketAwardsQuery, signal?: AbortSignal | null): Promise<IMarketAward[]> => {
    const { start, end } = pageQuery;

    const path = `/caiso/market-awards?${new URLSearchParams({
        start: start.toISOString(),
        end: end.toISOString(),
    })}`;

    const response = await API.fetch(path, {
        ...(signal && { signal }),
        method: 'get',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const respBody: any = await response.json();

    if (response.ok) {
        const items = respBody as IMarketAward[];

        return items.map(item => {
            const isExpired = isMarketAwardExpired(item);
            if (item.status === MarketAwardStatus.NEW && isExpired) {
                item.status = MarketAwardStatus.IGNORED;
            }

            item.earnings = calcMarketAwardEarnings(item);
            return item;
        });
    }

    throw new Error(respBody?.error?.message || `Cannot get CAISO market awards!`);
};

interface IDenyMarketAwards {
    ids: string[];
}

const denyMarketAwards = async (params: IDenyMarketAwards, signal?: AbortSignal | null): Promise<void> => {
    const { ids } = params;

    const path = `/caiso/market-awards/deny`;

    const response = await API.fetch(path, {
        ...(signal && { signal }),
        method: 'post',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            ids,
        }),
    });

    if (response.ok) {
        return;
    }

    const respBody: any = await response.json();
    throw new Error(respBody?.error?.message || `Cannot deny Market Awards!`);
};

interface IApproveMarketAwards {
    ids: string[];
    dispatch: {
        siteIds: number[];
    };
}

interface IApproveMarketAwardsResponse {
    dispatchId: string;
}

const approveMarketAwards = async (params: IApproveMarketAwards, signal?: AbortSignal | null): Promise<IApproveMarketAwardsResponse> => {
    const path = `/caiso/market-awards/approve`;

    const response = await API.fetch(path, {
        ...(signal && { signal }),
        method: 'post',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
    });

    if (response.ok) {
        const respBody: IApproveMarketAwardsResponse = await response.json();
        return respBody;
    }

    const respBody: any = await response.json();
    throw new Error(respBody?.error?.message || `Cannot approve Market Awards!`);
};

export const useCaisoMarketAwardsListQuery = (query: ICaisoMarketAwardsQuery, options = {}) => {
    return useQuery({
        queryKey: [QueryKey.CMRI_MARKET_AWARDS, query],
        queryFn: () => fetchCaisoMarketAwardsList(query),
        ...options,
    });
};

export const useCaisoMarketAwardsDeny = () => {
    const { notification } = App.useApp();
    const queryClient = useQueryClient();

    return useMutation<void, Error, IDenyMarketAwards>(denyMarketAwards, {
        onSuccess: async () => {
            await queryClient.invalidateQueries([QueryKey.CMRI_MARKET_AWARDS]);
            notification.success({ key: 'market-awards-deny-success', message: 'Market Awards Denied' });
        },
        onError: (error: Error) => {
            notification.error({ key: 'market-awards-deny-error', message: error.message || 'Cannot deny Market Awards' });
        },
    });
};

export const useCaisoMarketAwardsApprove = () => {
    const { notification } = App.useApp();
    const queryClient = useQueryClient();

    return useMutation<IApproveMarketAwardsResponse, Error, IApproveMarketAwards>(approveMarketAwards, {
        onSuccess: async () => {
            await queryClient.invalidateQueries([QueryKey.CMRI_MARKET_AWARDS]);
        },
        onError: (error: Error) => {
            notification.error({ key: 'market-awards-approve-error', message: error.message || 'Cannot approve Market Award' });
        },
    });
};
